import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react'
import { Check, Minus } from 'lucide-react'

/**
 * La casilla de la plataforma. Es la única que debe usarse: antes cada pantalla
 * traía su propio juego de clases y se veían distintas entre sí.
 *
 * Se dibuja con `appearance-none` en lugar de dejar la casilla nativa. El
 * modelo es la del login, que en realidad era la de Chrome —sus clases de
 * Tailwind (`text-[#3B5998]`, `border-gray-300`, `rounded`) no aplican a una
 * casilla nativa sin el plugin de forms, así que no hacían nada—. Chrome la
 * pinta con su color de sistema, #0075FF, y Safari y Firefox la pintan distinto:
 * al dibujarla aquí queda igual en los tres, con ese mismo azul.
 *
 * El anillo va con `focus-visible` y no con `focus`: así un clic con el mouse no
 * deja el anillo pegado —que es lo que hace Chrome y se ve como un error— pero
 * quien navega con el teclado sigue viendo dónde está parado.
 *
 * El blanco y el gris van como valor y no con `bg-white` / `border-gray-300`:
 * la hoja global pisa esas dos utilidades con `!important`
 * (`.bg-white { background-color: #FAFBFC !important }`), y con eso el azul del
 * estado marcado nunca se alcanzaba a ver. Escribir el color directo lo evita
 * sin sumar otro `!important`.
 *
 * Reenvía la ref y todos los props de un `input`, incluido `indeterminate`, que
 * se marca con una raya en vez de la palomita.
 */

type PropsCasilla = Omit<ComponentPropsWithoutRef<'input'>, 'type'> & {
  /** Texto al lado. Sin esto se pinta solo la casilla. */
  etiqueta?: ReactNode
  /** Clases para el contenedor cuando hay etiqueta. */
  claseContenedor?: string
  /**
   * Deshabilitada pero sin atenuar. Para las casillas que quedan bloqueadas
   * porque su fila ya se procesó: ahí el gris se lee como un error de carga.
   */
  conservarOpacidad?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, PropsCasilla>(
  (
    { etiqueta, claseContenedor = '', className = '', disabled, conservarOpacidad, ...props },
    ref,
  ) => {
    const casilla = (
      <span className="relative inline-flex flex-shrink-0 items-center justify-center">
        <input
          ref={ref}
          type="checkbox"
          disabled={disabled}
          className={`peer h-4 w-4 flex-shrink-0 appearance-none rounded border border-[#D1D5DB] bg-[#FFFFFF] transition-colors checked:border-[#0075FF] checked:bg-[#0075FF] indeterminate:border-[#0075FF] indeterminate:bg-[#0075FF] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0075FF]/40 disabled:cursor-not-allowed ${conservarOpacidad ? '' : 'disabled:opacity-50'} ${
            disabled ? '' : 'cursor-pointer'
          } ${className}`}
          {...props}
        />
        {/* Los iconos no reciben el clic: se lo deja todo a la casilla de abajo,
            que es la que carga con el foco y el teclado.

            Se centran sobre la casilla —`left-0` y 16px, su mismo tamaño— y no
            sobre el contenedor. Quien use el componente puede pasarle un
            margen por `className`, y ese margen crece el contenedor: centrar
            ahi corria la palomita fuera del cuadro azul. Pasaba con los `mr-2`
            de la solicitud de personal y con el `mt-1` de roles.

            La opacidad va en estos spans y no en el icono porque `peer-*` solo
            alcanza a los hermanos de la casilla, no a sus nietos. */}
        <span className="pointer-events-none absolute left-0 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0">
          <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
        </span>
        <span className="pointer-events-none absolute left-0 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center opacity-0 peer-indeterminate:opacity-100">
          <Minus className="h-3 w-3 text-white" strokeWidth={3.5} />
        </span>
      </span>
    )

    if (!etiqueta) return casilla

    return (
      <label
        className={`inline-flex items-center gap-2 ${
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        } ${claseContenedor}`}
      >
        {casilla}
        <span className="text-sm text-gray-700">{etiqueta}</span>
      </label>
    )
  },
)

Checkbox.displayName = 'Checkbox'
export default Checkbox
